import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// Retry wrapper: if a lazy chunk fails to load (stale deployment), reload the page
// so the browser fetches fresh index.html with updated chunk hashes.
const lazyWithRetry = (componentImport) =>
   lazy(async () => {
      try {
         const component = await componentImport();
         return component;
      } catch (error) {
         const isChunkLoadError =
            error?.message?.includes('Failed to fetch dynamically imported module') ||
            error?.message?.includes('Importing a module script failed') ||
            error?.message?.includes('Failed to load module script') ||
            error?.name === 'ChunkLoadError';

         if (isChunkLoadError) {
            // Reload the page to get fresh chunk hashes from new deployment
            window.location.reload();
            // Return a blank component while the reload happens
            return { default: () => null };
         }
         throw error;
      }
   });

const ScrollToTopButton = lazyWithRetry(() => import("./Components/ScrollToTopButton"));
const UpdateBanner = lazyWithRetry(() => import("./Components/UpdateBanner"));
const FeedbackPopup = lazyWithRetry(() => import("./Components/FeedbackPopup"));
import { useDispatch } from "react-redux";
import { getUser } from "./dashboard/userSlice";
import { RedirectIfLoggedIn, RequireAuth } from "./RouteGuards";
import { PageNotFound } from "./PageNotFound";
import Success from "./landing Page/Auth/Signup-Verification/Success";
import LandingLayout from "./layouts/LandingLayout";

// Lazy-loaded Landing Pages
const LandingPage = lazyWithRetry(() => import("./landing Page/Home/index"));
const AboutPage = lazyWithRetry(() => import("./landing Page/About/index"));
const FaqPage = lazyWithRetry(() => import("./landing Page/page/FaqPage"));
const FeaturesPage = lazyWithRetry(() => import("./landing Page/page/FeaturesPage"));
const PricePage = lazyWithRetry(() => import("./landing Page/page/PricePage"));
const DemoPage = lazyWithRetry(() => import("./landing Page/page/DemoPage"));
const TemplatesPage = lazyWithRetry(() => import("./landing Page/page/TemplatesPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./landing Page/page/Policies/PrivacyPolicyPage"));
const LandingContact = lazyWithRetry(() => import("./landing Page/Contact/index"));
const IntegrationsPage = lazyWithRetry(() => import("./landing Page/page/IntegrationsPage"));
const ApiPage = lazyWithRetry(() => import("./landing Page/page/ApiPage"));
const QualitativePage = lazyWithRetry(() => import("./landing Page/page/QualitativePage"));
const QuantitativePage = lazyWithRetry(() => import("./landing Page/page/QuantitativePage"));
const DataCleaningPage = lazyWithRetry(() => import("./landing Page/page/DataCleaningPage"));
const DataVisualizationPage = lazyWithRetry(() => import("./landing Page/page/DataVisualizationPage"));
const PdfAnalysisPage = lazyWithRetry(() => import("./landing Page/page/PdfAnalysisPage"));
const DataIntegrationPage = lazyWithRetry(() => import("./landing Page/page/DataIntegrationPage"));
const AiAgentPage = lazyWithRetry(() => import("./landing Page/page/AiAgentPage"));
const SolutionsPage = lazyWithRetry(() => import("./landing Page/page/SolutionsPage"));
const SurveyDataAnalysisPage = lazyWithRetry(() => import("./landing Page/page/SurveyDataAnalysisPage"));
const ThesisDissertationPage = lazyWithRetry(() => import("./landing Page/page/ThesisDissertationPage"));
const ExcelDataAnalysisPage = lazyWithRetry(() => import("./landing Page/page/ExcelDataAnalysisPage"));
const ResearchDataAnalysisPage = lazyWithRetry(() => import("./landing Page/page/ResearchDataAnalysisPage"));
const CustomerFeedbackAnalysisPage = lazyWithRetry(() => import("./landing Page/page/CustomerFeedbackAnalysisPage"));
const InterviewTranscriptAnalysisPage = lazyWithRetry(() => import("./landing Page/page/InterviewTranscriptAnalysisPage"));
const StatisticalAnalysisPage = lazyWithRetry(() => import("./landing Page/page/StatisticalAnalysisPage"));

// Lazy-loaded Auth Pages
const Login = lazyWithRetry(() => import("./landing Page/Auth/Login"));
const Signup = lazyWithRetry(() => import("./landing Page/Auth/Signup"));
const EmailVerification = lazyWithRetry(() => import("./landing Page/Auth/Signup-Verification"));
const ResetPassword = lazyWithRetry(() => import("./landing Page/Auth/Reset-Password"));

// Dashboard Pages
const DashboardContact = lazyWithRetry(() => import("./Components/ContactUs"));
const Welcome = lazyWithRetry(() => import("./dashboard/welcome"));
const StepAnalysis = lazyWithRetry(() => import("./dashboard/welcome/StepAnalysis"));
const QuantittiveFileUpload = lazyWithRetry(() => import("./dashboard/analysis/qunatitative-analysis/QuantittiveFileUpload"));
const DataDashboard = lazyWithRetry(() => import("./dashboard/data-dashboard/DataDashboard"));
const TableConversation = lazyWithRetry(() => import("./dashboard/table-conversation/TableConversation"));
const Layout = lazyWithRetry(() => import("./dashboard/Layout"));
const Analysis = lazyWithRetry(() => import("./dashboard/analysis"));
const Subscription = lazyWithRetry(() => import("./dashboard/subscription"));
const BasicTabs = lazyWithRetry(() => import("./dashboard/account-info"));
const DataCleaning = lazyWithRetry(() => import("./dashboard/data-cleaning/DataCleaning"));
const PdfChat = lazyWithRetry(() => import("../src/dashboard/PDF Chat/Pdfchat"));
const DataIntegration = lazyWithRetry(() => import("./dashboard/DataIntegration"));
const AgentPage = lazyWithRetry(() => import("./dashboard/agent/AgentPage"));
const MathSolver = lazyWithRetry(() => import("./dashboard/math-solver/MathSolver"));
const Presentations = lazyWithRetry(() => import("./dashboard/presentations/Presentations"));
const Notebooks = lazyWithRetry(() => import("./dashboard/notebooks/Notebooks"));
const AIToolkit = lazyWithRetry(() => import("./dashboard/ai-toolkit/AIToolkit"));
const AIStudio = lazyWithRetry(() => import("./dashboard/ai-toolkit/AIStudio"));
const SharedInsightPage = lazyWithRetry(() => import("./dashboard/agent/SharedInsightPage"));
const AuthCallback = lazyWithRetry(() => import("./AuthCallback"));
const TermsPage = lazyWithRetry(() => import("./landing Page/page/Policies/TermsPage"));
const CookiePolicyPage = lazyWithRetry(() => import("./landing Page/page/Policies/CookiePolicyPage"));
const AiDataPolicyPage = lazyWithRetry(() => import("./landing Page/page/Policies/AiDataUsagePolicyPage"));
const BlogPage = lazyWithRetry(() => import("./landing Page/page/BlogPage"));
const BlogPostPage = lazyWithRetry(() => import("./landing Page/page/BlogPostPage"));

const ScrollToTop = () => {
   const { pathname } = useLocation();

   useEffect(() => {
      // Scroll to top whenever pathname changes
      window.scrollTo(0, 0);
   }, [pathname]);

   return null;
};

function App() {
   const dispatch = useDispatch();
   const location = useLocation();
   const [showUpdateBanner, setShowUpdateBanner] = useState(false);
   const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

   const isLandingPage = [
      "/",
      "/about",
      "/faq",
      "/login",
      "/signup",
      "/email-verification",
      "/reset-password",
      "/privacy-policy",
      "/terms-and-conditions",
      "/cookie-policy",
      "/ai-data-usage-policy",
      "/contact-us",
      "/blogs",
      // "/integrations",
      "/features/qualitative-data-analysis",
      "/features/quantitative-data-analysis",
      "/features/data-visualization-dashboard",
      "/api-docs",
   ].includes(location.pathname);

   // ─── Feedback Popup: show once per login session ───────────────────────────
   // ─── Feedback Popup: show every 60s cycle ──────────────────────────────────
   const [feedbackCycle, setFeedbackCycle] = useState(0); // 👈 cycle counter

   useEffect(() => {
      const email = localStorage.getItem("email");
      if (!email) return;

      const flagKey = `feedbackShown_${email}`;
      const hasSeenFeedback = sessionStorage.getItem(flagKey);
      if (hasSeenFeedback) return;

      // 10 minute delay → show popup
      const showTimer = setTimeout(() => {
         setShowFeedbackPopup(true);
         sessionStorage.setItem(flagKey, "true");

         // 50s later → clear flag & bump cycle counter to re-run effect
         const resetTimer = setTimeout(() => {
            sessionStorage.removeItem(flagKey);
            setFeedbackCycle(prev => prev + 1); // 👈 this re-triggers the useEffect
         }, 28800000);
         return () => clearTimeout(resetTimer);
      }, 600000);
      return () => clearTimeout(showTimer);
   }, [location.pathname, feedbackCycle]); // 👈 added feedbackCycle as dependency
   // ───────────────────────────────────────────────────────────────────────────
   // ───────────────────────────────────────────────────────────────────────────

   const handleCloseFeedback = () => {
      setShowFeedbackPopup(false);
   };
   // ───────────────────────────────────────────────────────────────────────────

   // Check for new version every 30 seconds
   useEffect(() => {
      let currentVersion = null;

      const checkForUpdates = async () => {
         try {
            // Add timestamp to prevent caching
            const response = await fetch('/version.json?' + Date.now());
            const data = await response.json();

            if (currentVersion === null) {
               // First load - store the version
               currentVersion = data.version;
               const storedVersion = localStorage.getItem('appVersion');
               if (!storedVersion) {
                  localStorage.setItem('appVersion', data.version);
               }
            } else if (currentVersion !== data.version) {
               // Version changed - check against stored version
               const storedVersion = localStorage.getItem('appVersion');
               if (storedVersion !== data.version) {
                  console.log('New version detected!');
                  setShowUpdateBanner(true);
               }
            }
         } catch (error) {
            console.error('Failed to check for updates:', error);
         }
      };

      // Check after 6 seconds on initial load to avoid blocking the critical request chain
      const initialDelay = setTimeout(checkForUpdates, 6000);

      // Then check every 30 seconds
      const interval = setInterval(checkForUpdates, 30 * 1000);

      return () => {
         clearTimeout(initialDelay);
         clearInterval(interval);
      };
   }, []);

   // Handle update button click
   const handleUpdate = () => {
      // Update the stored version before reloading
      fetch('/version.json?' + Date.now())
         .then(res => res.json())
         .then(data => {
            localStorage.setItem('appVersion', data.version);
            window.location.reload();
         })
         .catch(() => {
            // If fetch fails, just reload anyway
            window.location.reload();
         });
   };

   // Handle close button click
   const handleCloseBanner = () => {
      setShowUpdateBanner(false);
   };

   useEffect(() => {
      const email = localStorage.getItem("email");
      if (email) {
         dispatch(getUser());
      }
   }, [dispatch]);

   return (
      <>
         <ScrollToTop />
         <Suspense fallback={null}>
            {isLandingPage && <ScrollToTopButton />}

            {/* Update Banner */}
            {showUpdateBanner && (
               <UpdateBanner
                  onUpdate={handleUpdate}
                  onClose={handleCloseBanner}
               />
            )}

            {/* Feedback Popup — shown 10s after login, once per session */}
            {showFeedbackPopup && (
               <FeedbackPopup
                  onClose={handleCloseFeedback}
               />
            )}
         </Suspense>

         <Suspense
            fallback={
               <div style={{ textAlign: "center", marginTop: "50px" }}>
                  Loading...
               </div>
            }
         >
            <Routes>
               {/* Landing Routes */}

               <Route
                  path="/"
                  element={
                     <LandingLayout>
                        <LandingPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="features"
                  element={
                     <LandingLayout>
                        <FeaturesPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/about"
                  element={
                     <LandingLayout>
                        <AboutPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/pricing"
                  element={
                     <LandingLayout>
                        <PricePage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/faq"
                  element={
                     <LandingLayout>
                        <FaqPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="demo"
                  element={
                     <LandingLayout>
                        <DemoPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/templates"
                  element={
                     <LandingLayout>
                        <TemplatesPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/login"
                  element={
                     <RedirectIfLoggedIn>
                        <Login />
                     </RedirectIfLoggedIn>
                  }
               />
               <Route
                  path="/signup"
                  element={
                     <RedirectIfLoggedIn>
                        <Signup />
                     </RedirectIfLoggedIn>
                  }
               />
               <Route
                  path="/email-verification"
                  element={
                     <RedirectIfLoggedIn>
                        <EmailVerification />
                     </RedirectIfLoggedIn>
                  }
               />
               <Route
                  path="/reset-password"
                  element={
                     <RedirectIfLoggedIn>
                        <ResetPassword />
                     </RedirectIfLoggedIn>
                  }
               />
               <Route
                  path="/privacy-policy"
                  element={<LandingLayout><PrivacyPolicyPage /></LandingLayout>}
               />
               <Route path="/terms-and-conditions" element={<LandingLayout><TermsPage /></LandingLayout>} />
               <Route path="/cookie-policy" element={<LandingLayout><CookiePolicyPage /></LandingLayout>} />
               <Route path="/ai-data-usage-policy" element={<LandingLayout><AiDataPolicyPage /></LandingLayout>} />
               <Route
                  path="/integrations"
                  element={<Navigate to="/features/data-integration" replace />}
               />
               <Route
                  path="/contact-us"
                  element={
                     <LandingLayout>
                        <LandingContact />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/api-docs"
                  element={
                     <LandingLayout>
                        <ApiPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/blogs"
                  element={
                     <LandingLayout>
                        <BlogPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/blogs/:slug"
                  element={
                     <LandingLayout>
                        <BlogPostPage />
                     </LandingLayout>
                  }
               />

               {/* Feature Landing Pages */}
               <Route
                  path="/features/qualitative-data-analysis"
                  element={
                     <LandingLayout>
                        <QualitativePage />
                     </LandingLayout>
                  }
               />

               <Route
                  path="/features/quantitative-data-analysis"
                  element={
                     <LandingLayout>
                        <QuantitativePage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/features/data-cleaning"
                  element={
                     <LandingLayout>
                        <DataCleaningPage />
                     </LandingLayout>
                  }
               />

               <Route
                  path="/features/data-visualization-dashboard"
                  element={
                     <LandingLayout>
                        <DataVisualizationPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/features/pdf-analysis"
                  element={
                     <LandingLayout>
                        <PdfAnalysisPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/features/data-integration"
                  element={
                     <LandingLayout>
                        <DataIntegrationPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/ai-agent"
                  element={
                     <LandingLayout>
                        <AiAgentPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/features/ai-data-analyst-agent"
                  element={<Navigate to="/ai-agent" replace />}
               />
               <Route
                  path="/solutions"
                  element={
                     <LandingLayout>
                        <SolutionsPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/solutions/survey-data-analysis"
                  element={
                     <LandingLayout>
                        <SurveyDataAnalysisPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/solutions/thesis-dissertation-data-analysis"
                  element={
                     <LandingLayout>
                        <ThesisDissertationPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/solutions/excel-data-analysis"
                  element={
                     <LandingLayout>
                        <ExcelDataAnalysisPage />
                     </LandingLayout>
                  }
               />
               <Route
                  path="/solutions/research-data-analysis"
                  element={
                     <LandingLayout>
                        <ResearchDataAnalysisPage />
                     </LandingLayout>
                  }
               />
               <Route
                   path="/solutions/customer-feedback-analysis"
                   element={
                      <LandingLayout>
                         <CustomerFeedbackAnalysisPage />
                      </LandingLayout>
                   }
                />
               <Route
                   path="/solutions/interview-transcript-analysis"
                   element={
                      <LandingLayout>
                         <InterviewTranscriptAnalysisPage />
                      </LandingLayout>
                   }
                />
               <Route
                   path="/solutions/statistical-analysis"
                   element={
                      <LandingLayout>
                         <StatisticalAnalysisPage />
                      </LandingLayout>
                   }
                />

               {/* Dashboard Routes */}
               <Route
                  path="/welcome"
                  element={
                     <RequireAuth>
                        <Layout children={<Welcome />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/step-analysis"
                  element={
                     <RequireAuth>
                        <Layout children={<StepAnalysis />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/quantittive-upload-file"
                  element={
                     <RequireAuth>
                        <Layout children={<QuantittiveFileUpload />} />
                     </RequireAuth>
                  }
               />

               <Route
                  path="/analysis"
                  element={
                     <RequireAuth>
                        <Layout children={<Analysis />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/subscription"
                  element={
                     <RequireAuth>
                        <Layout children={<Subscription />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/profile"
                  element={
                     <RequireAuth>
                        <Layout children={<BasicTabs />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/dashboard/contact-us"
                  element={
                     <RequireAuth>
                        <Layout children={<DashboardContact />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/data-cleaning"
                  element={
                     <RequireAuth>
                        <Layout children={<DataCleaning />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/data-dashboard"
                  element={
                     <RequireAuth>
                        <Layout children={<DataDashboard />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/table-conversation"
                  element={
                     <RequireAuth>
                        <Layout children={<TableConversation />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/pdf-chat"
                  element={
                     <RequireAuth waitForRestore>
                        <Layout children={<PdfChat />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/data-integrations"
                  element={
                     <RequireAuth>
                        <Layout children={<DataIntegration />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/ai-toolkit"
                  element={
                     <RequireAuth>
                        <Layout children={<AIToolkit />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/ai-studio"
                  element={
                     <RequireAuth>
                        <Layout children={<AIStudio />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/library"
                  element={<Navigate replace to={`/pdf-chat?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(location.search)), tab: "reports" })}`} />}
               />
               <Route
                  path="/agent"
                  element={
                     <RequireAuth>
                        <Layout children={<AgentPage />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/math-solver"
                  element={
                     <RequireAuth>
                        <Layout children={<MathSolver />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/presentations"
                  element={
                     <RequireAuth>
                        <Layout children={<Presentations />} />
                     </RequireAuth>
                  }
               />
               <Route
                  path="/notebooks"
                  element={
                     <RequireAuth>
                        <Layout children={<Notebooks />} />
                     </RequireAuth>
                  }
               />
               <Route path="/auth/callback" element={<AuthCallback />} />
               <Route path="/success-msg" element={<Success />} />
               <Route path="/insight/:id" element={<SharedInsightPage />} />
               <Route path="/404" element={<PageNotFound />} />
               <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
         </Suspense>
      </>
   );
}

export default App;
